import { memo } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { AiSuggestedQuery } from "@/feature/ai-planner/types/ai-chat.types";
import type { ChatClarificationData } from "../types/chat-ui.types";

function toDisplayLabel(value: string) {
  if (!value) return value;
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export const ClarificationSuggestions = memo(function ClarificationSuggestions({
  clarification,
  onSelect,
  disabled = false,
  pendingQueryId,
}: {
  clarification?: ChatClarificationData;
  onSelect?: (query: AiSuggestedQuery) => void;
  disabled?: boolean;
  pendingQueryId?: string | null;
}) {
  if (
    !clarification ||
    (!clarification.fields.length && !clarification.missing.length)
  ) {
    return null;
  }

  return (
    <div className="mt-3 space-y-3">
      {clarification.fields.map((field) => (
        <section
          key={field.key}
          className="rounded-2xl border border-border/70 bg-card/70 px-3 py-3"
        >
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium">{toDisplayLabel(field.label)}</p>
            {field.fieldKeys.length > 1 ? (
              <Badge
                variant="secondary"
                className="h-auto rounded-full px-2 py-0.5 text-[10px]"
              >
                Combined
              </Badge>
            ) : null}
          </div>

          {field.suggestions.length && onSelect ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {field.suggestions.map((suggestion) => {
                const isPending = pendingQueryId === suggestion.id;

                return (
                  <Button
                    key={suggestion.id}
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={disabled || isPending}
                    className="h-auto max-w-full rounded-full border-border/70 bg-background/80 px-3 py-1.5 text-left text-xs font-medium whitespace-normal shadow-none hover:bg-muted/70"
                    onClick={() => onSelect(suggestion)}
                  >
                    {isPending ? "Sending..." : suggestion.label}
                  </Button>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground mt-2 text-xs">
              Reply with your {field.label.toLowerCase()}.
            </p>
          )}
        </section>
      ))}
    </div>
  );
});
